import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { CheckCheck, Filter } from "lucide-react";
import {
  PageActionsProvider,
  usePageActionsState,
  usePageFilterToggleState,
  useResetFilterToggleOnPathChange,
} from "@/app/pageActions/PageActionsContext";
import { PageActionsToolbar } from "@/app/pageActions/PageActionsToolbar";
import { OpsShell } from "@/app/shells/OpsShell";
import {
  useExeBottomNav,
  useFieldBottomNav,
} from "@/app/nav/useShellBottomNav";
import { FieldNotifyBellButton } from "@/modules/sys/components/FieldNotifyBellButton";
import { useOnlineStatus } from "@/offline";
import { platform } from "@/platform";
import { cn } from "@/shared/lib/cn";
import { setDocumentTitle } from "@/shared/lib/useDocumentTitle";
import { readReturnTo } from "@/shared/shell/returnTo";
import { Banner, BottomNavContext, BottomNavigation, Button, MobileHeader } from "@/shared/ui";
import { IconButton } from "@/shared/ui/core";

export { OpsShell };

function FieldPageActionsBar() {
  const actions = usePageActionsState();
  const toolbarActions = actions.filter(
    (a) => a.id !== "back" && a.id !== "mark-all-read",
  );
  if (toolbarActions.length === 0) return null;
  return (
    <div className="sticky top-[calc(var(--control-height-touch)+env(safe-area-inset-top))] z-[var(--z-sticky)] border-b border-border/60 bg-surface px-3 py-2">
      <PageActionsToolbar clearBottomNav hideFilterToggle className="w-full" />
    </div>
  );
}

export function FieldShell() {
  return (
    <PageActionsProvider>
      <FieldShellContent />
    </PageActionsProvider>
  );
}

function FieldShellContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  useResetFilterToggleOnPathChange(path);
  const online = useOnlineStatus();
  const showOfflineBanner = !online && !platform.isNative && !path.startsWith("/field/sync");
  const filterToggle = usePageFilterToggleState();

  const isHome = path === "/field/home" || path === "/field";
  const isToday = path === "/field/today";
  const isBatches = path === "/field/batches";
  const isBatchDetail = /^\/field\/batches\//.test(path);
  const isExecDetail = /^\/field\/exec\//.test(path);
  const isIpqcDetail = /^\/field\/ipqc\//.test(path);
  const isFormDetail = /^\/field\/forms\//.test(path);
  const isActivity = path.startsWith("/field/activity");
  const isNotifications = path.startsWith("/field/notifications");
  const isSettings = path.startsWith("/field/settings");
  const isSync = path.startsWith("/field/sync");
  const isProfile = path.startsWith("/field/profile");
  const isHandoverDetail =
    path.startsWith("/field/handover") &&
    location.search.includes("fromOp=");

  const isWmsHub = path === "/field/wms";
  const isWmsMove = path.startsWith("/field/wms/move");
  const isWmsCount = path.startsWith("/field/wms/count");
  const isWmsStock = path.startsWith("/field/wms/stock");
  const isWmsExpiry = path.startsWith("/field/wms/expiry");
  const isWmsCreate = isWmsMove || isWmsCount;
  const isWmsPeek = isWmsStock || isWmsExpiry;
  const isPurRequisitions = path.startsWith("/field/pur");
  const isPurNew = path === "/field/pur/requisitions/new";
  const isPurDetail = /^\/field\/pur\/requisitions\//.test(path) && !isPurNew;
  const isQms = path.startsWith("/field/qms");
  const isQmsIqcDetail = path.startsWith("/field/qms/iqc/");
  const isQmsFqcDetail = path.startsWith("/field/qms/fqc/");
  const isQmsDetail = isQmsIqcDetail || isQmsFqcDetail || /^\/field\/qms\/[^/]+\/[^/]+/.test(path);

  const headerTitle = isQmsIqcDetail
    ? "Đánh giá chất lượng đầu vào"
    : isQmsFqcDetail
      ? "Đánh giá chất lượng đầu ra"
      : isQmsDetail
        ? "Chi tiết kiểm tra"
        : isQms
          ? "Kiểm tra chất lượng"
          : path.startsWith("/field/me")
            ? "Tài khoản"
      : isProfile
        ? "Hồ sơ"
        : isPurDetail
          ? "Chi tiết đề nghị mua"
        : isPurRequisitions
          ? "Đề nghị mua hàng"
          : isBatchDetail
        ? "Chi tiết mẻ"
        : isBatches
          ? "Mẻ sản xuất"
        : path.startsWith("/field/scan")
        ? "Quét"
        : path.startsWith("/field/cip")
          ? "Vệ sinh chuyền"
          : path.startsWith("/field/handover")
            ? "Bàn giao"
            : path.startsWith("/field/rework")
              ? "Gia công lại"
              : path.startsWith("/field/usage")
                ? "Hao hụt"
              : isWmsMove
                ? "Chuyển vị trí"
                : isWmsCount
                  ? "Kiểm kê"
                  : isWmsStock
                    ? "Tồn kho"
                    : isWmsExpiry
                      ? "Hạn dùng"
                      : isWmsHub
                        ? "Kho hiện trường"
                        : isActivity
                          ? "Việc tôi đã làm"
                          : isNotifications
                            ? "Thông báo"
                            : isSettings
                              ? "Cài đặt ứng dụng"
                              : isSync
                                ? "Đồng bộ"
                                : isIpqcDetail
                                  ? "IPQC sàng lọc"
                                  : isFormDetail
                                    ? "Biểu mẫu"
                                    : isExecDetail
                                      ? "Công đoạn"
                                      : isToday
                                        ? "Hàng đợi"
                                        : "Trang chủ";

  useEffect(() => {
    setDocumentTitle(headerTitle);
  }, [headerTitle]);

  const showBack =
    isQms ||
    isToday ||
    isBatchDetail ||
    isExecDetail ||
    isHandoverDetail ||
    isIpqcDetail ||
    isFormDetail ||
    isActivity ||
    isNotifications ||
    isSettings ||
    isSync ||
    isProfile ||
    isPurDetail ||
    isPurNew ||
    path.startsWith("/field/wms") ||
    path.startsWith("/field/hr") ||
    path.startsWith("/field/work") ||
    path.startsWith("/field/cip") ||
    path.startsWith("/field/usage") ||
    path.startsWith("/field/rework") ||
    path.startsWith("/field/handover");

  const isTopLevelTab =
    isHome ||
    isToday ||
    isBatches ||
    path === "/field/scan" ||
    path === "/field/pur/requisitions" ||
    path === "/field/me" ||
    isWmsHub;

  const showBottomNav = isTopLevelTab;

  const { items, errorBanner } = useFieldBottomNav();

  const activeId = path.startsWith("/field/scan")
    ? "scan"
    : path.startsWith("/field/batches")
      ? "batches"
      : path.startsWith("/field/pur")
      ? "requisitions"
      : path.startsWith("/field/cip")
      ? "cip"
      : path.startsWith("/field/me") ||
          path.startsWith("/field/profile") ||
          path.startsWith("/field/activity") ||
          path.startsWith("/field/notifications") ||
          path.startsWith("/field/settings") ||
          path.startsWith("/field/sync") ||
          path.startsWith("/field/handover") ||
          path.startsWith("/field/rework") ||
          path.startsWith("/field/usage") ||
          path.startsWith("/field/wms")
        ? "account"
        : isToday
          ? null
          : isHome || path.startsWith("/field/exec") || path.startsWith("/field/ipqc") || path.startsWith("/field/forms")
            ? "home"
            : path.startsWith("/field")
              ? "home"
              : null;

  const returnTo = readReturnTo(location.state);

  const actions = usePageActionsState();
  const customBack = actions.find((a) => a.id === "back");

  const markAllAction = actions.find((a) => a.id === "mark-all-read");

  const hasTrailing = Boolean(
    (!isQms && filterToggle) || isHome || isToday || markAllAction,
  );
  const trailing = hasTrailing ? (
    <div className="flex items-center gap-1">
      {markAllAction ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={markAllAction.disabled}
          onClick={markAllAction.onClick}
          className="h-8 gap-1 px-2 text-caption font-semibold text-on-primary hover:bg-white/15 active:bg-white/25 hover:text-on-primary"
          leftIcon={<CheckCheck className="size-4 text-on-primary" />}
        >
          {markAllAction.label}
        </Button>
      ) : null}
      {!isQms && filterToggle ? (
        <div className="relative flex items-center">
          <IconButton
            tooltip={false}
            label={filterToggle.open ? "Ẩn bộ lọc" : "Hiện bộ lọc"}
            icon={<Filter className="size-5" />}
            size="touch"
            variant="ghost"
            onClick={filterToggle.onToggle}
            className="text-on-primary bg-transparent hover:bg-transparent active:bg-transparent hover:opacity-85 active:opacity-70 transition-opacity"
          />
          {filterToggle.activeCount && filterToggle.activeCount > 0 ? (
            <span className="pointer-events-none absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-on-accent shadow-sm">
              {filterToggle.activeCount}
            </span>
          ) : null}
        </div>
      ) : null}
      {isHome || isToday ? (
        <FieldNotifyBellButton
          from={isHome ? "/field/home" : "/field/today"}
        />
      ) : null}
    </div>
  ) : undefined;

  return (
    <div className="app-frame flex min-h-svh flex-col bg-bg text-text">
      <MobileHeader
        title={headerTitle}
        tint="field"
        showBack={showBack}
        trailing={trailing}
        onBack={() => {
          if (customBack?.onClick) {
            customBack.onClick();
            return;
          }
          if (isPurNew) {
            navigate("/field/pur/requisitions", { replace: true });
            return;
          }
          if (isPurRequisitions && !isPurDetail) {
            if (returnTo) {
              navigate(returnTo, { replace: true });
              return;
            }
            navigate("/field/home", { replace: true });
            return;
          }
          if (
            window.history.state &&
            typeof window.history.state.idx === "number" &&
            window.history.state.idx > 0
          ) {
            navigate(-1);
            return;
          }
          if (returnTo) {
            navigate(returnTo, { replace: true });
            return;
          }
          if (isToday) {
            navigate("/field/home", { replace: true });
            return;
          }
          if (isBatchDetail) {
            navigate("/field/batches", { replace: true });
            return;
          }
          if (isIpqcDetail) {
            const m = path.match(/^\/field\/ipqc\/([^/]+)/);
            if (m?.[1]) {
              navigate(`/field/exec/${m[1]}`, { replace: true });
              return;
            }
          }
          if (isFormDetail) {
            const op = new URLSearchParams(location.search).get("op");
            if (op) {
              navigate(`/field/exec/${encodeURIComponent(op)}`, { replace: true });
              return;
            }
          }
          if (isActivity || isProfile) {
            navigate("/field/me", { replace: true });
            return;
          }
          if (isNotifications) {
            navigate("/field/home", { replace: true });
            return;
          }
          if (isSettings || isSync) {
            navigate("/field/me", { replace: true });
            return;
          }
          if (
            path.startsWith("/field/usage") ||
            path.startsWith("/field/rework") ||
            path.startsWith("/field/handover")
          ) {
            navigate("/field/me", { replace: true });
            return;
          }
          if (path.startsWith("/field/cip")) {
            navigate("/field/home", { replace: true });
            return;
          }
          if (isWmsCreate || isWmsPeek) {
            navigate("/field/wms", { replace: true });
            return;
          }
          if (isWmsHub || path.startsWith("/field/hr") || path.startsWith("/field/work")) {
            navigate("/field/me", { replace: true });
            return;
          }
          if (isPurDetail) {
            navigate("/field/pur/requisitions", { replace: true });
            return;
          }
          if (isPurRequisitions) {
            navigate("/field/home", { replace: true });
            return;
          }
          if (isQmsDetail) {
            if (
              window.history.state &&
              typeof window.history.state.idx === "number" &&
              window.history.state.idx > 0
            ) {
              navigate(-1);
              return;
            }
            const isIqc = path.includes("/qms/iqc/");
            navigate(`/field/qms?tab=${isIqc ? "iqc" : "fqc"}`, { replace: true });
            return;
          }
          if (isQms) {
            if (
              window.history.state &&
              typeof window.history.state.idx === "number" &&
              window.history.state.idx > 0
            ) {
              navigate(-1);
              return;
            }
            navigate("/field/home", { replace: true });
            return;
          }
          navigate("/field/home", { replace: true });
        }}
      />
        <FieldPageActionsBar />
        {showOfflineBanner ? (
          <div className="border-b border-border/60 bg-surface px-3 py-2">
            <Banner
              tone="warning"
              title="Đang offline"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate("/field/sync")}
                >
                  Sync
                </Button>
              }
            />
          </div>
        ) : null}
        {errorBanner ? (
          <div className="border-b border-border/60 bg-surface px-3 py-2">
            <Banner tone="warning" title={errorBanner} />
          </div>
        ) : null}
        <BottomNavContext.Provider value={showBottomNav}>
          <main
            className={cn(
              "mx-auto w-full max-w-3xl min-h-0 flex-1 overflow-y-auto px-4 pt-4",
              showBottomNav
                ? "pb-[calc(var(--layout-bottom-nav-height)+env(safe-area-inset-bottom)+1rem)]"
                : "pb-[calc(env(safe-area-inset-bottom)+1rem)]",
            )}
          >
            <Outlet />
          </main>
          {showBottomNav ? (
            <BottomNavigation items={items} activeId={activeId} />
          ) : null}
        </BottomNavContext.Provider>
      </div>
    );
  }

export function ExeShell() {
  return (
    <PageActionsProvider>
      <ExeShellContent />
    </PageActionsProvider>
  );
}

function ExeShellContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { items, errorBanner } = useExeBottomNav();
  const actions = usePageActionsState();
  const markAllAction = actions.find((a) => a.id === "mark-all-read");

  const path = location.pathname;
  const isAccount =
    path.startsWith("/exe/me") ||
    path.startsWith("/exe/profile") ||
    path.startsWith("/exe/settings") ||
    path.startsWith("/exe/notifications");
  const isApprovalDetail = /^\/exe\/approvals\/[^/]+/.test(path);
  const isRef = path.startsWith("/exe/ref/");
  const isExeQms = path.startsWith("/exe/qms");
  const isExeQmsIqcDetail = path.startsWith("/exe/qms/iqc/");
  const isExeQmsFqcDetail = path.startsWith("/exe/qms/fqc/");
  const isExeQmsDetail = isExeQmsIqcDetail || isExeQmsFqcDetail || /^\/exe\/qms\/[^/]+\/[^/]+/.test(path);

  const isTopLevelExeTab =
    path === "/exe" ||
    path === "/exe/" ||
    path === "/exe/home" ||
    path === "/exe/approvals" ||
    path === "/exe/alerts" ||
    path === "/exe/reports" ||
    path === "/exe/me";

  const showBottomNav = isTopLevelExeTab;

  const activeId = path.startsWith("/exe/approvals")
    ? "approve"
    : path.startsWith("/exe/alerts") || isRef
      ? "alert"
      : path.startsWith("/exe/reports")
        ? "report"
        : isAccount
          ? "account"
          : "home";

  const refKind = isRef ? path.split("/")[3] ?? "" : "";
  const refTitle =
    refKind === "quality" || refKind === "qms"
      ? "Kiểm tra chất lượng"
      : refKind === "holds"
        ? "HOLD"
        : refKind === "lots"
          ? "Lô"
          : refKind === "expiry"
            ? "Hạn dùng"
            : refKind === "ncrs"
              ? "NCR"
              : "Chi tiết";

  const title = isExeQmsIqcDetail
    ? "Đánh giá chất lượng đầu vào"
    : isExeQmsFqcDetail
      ? "Đánh giá chất lượng đầu ra"
      : isExeQmsDetail
        ? "Chi tiết kiểm tra"
        : isExeQms
          ? "Kiểm tra chất lượng"
          : isApprovalDetail
            ? "Chi tiết duyệt"
    : isRef
      ? refTitle
      : path.startsWith("/exe/approvals")
        ? "Duyệt"
        : path.startsWith("/exe/alerts")
          ? "Cảnh báo"
          : path.startsWith("/exe/reports")
            ? "Báo cáo"
            : path.startsWith("/exe/notifications")
              ? "Thông báo"
              : path.startsWith("/exe/profile")
                ? "Hồ sơ"
                : path.startsWith("/exe/settings")
                  ? "Cài đặt"
                  : path.startsWith("/exe/me")
                    ? "Tài khoản"
                    : "Trang chủ";

  useEffect(() => {
    setDocumentTitle(title);
  }, [title]);

  const returnTo = readReturnTo(location.state);

  const showBack =
    isExeQms ||
    isApprovalDetail ||
    isRef ||
    path.startsWith("/exe/profile") ||
    path.startsWith("/exe/settings") ||
    path.startsWith("/exe/notifications") ||
    Boolean(returnTo);

  const backTo = returnTo
    ? returnTo
    : isExeQmsDetail
      ? "/exe/qms"
      : isExeQms
        ? "/exe"
        : isApprovalDetail
          ? "/exe/approvals"
        : isRef
          ? "/exe/alerts"
          : path.startsWith("/exe/me")
            ? "/exe"
            : "/exe/me";

  return (
    <div className="app-frame flex min-h-svh flex-col bg-bg text-text">
      <MobileHeader
        title={title}
        tint="field"
        showBack={showBack}
        onBack={() => {
          if (
            window.history.state &&
            typeof window.history.state.idx === "number" &&
            window.history.state.idx > 0
          ) {
            navigate(-1);
            return;
          }
          navigate(backTo, { replace: true });
        }}
        trailing={
          markAllAction ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={markAllAction.disabled}
              onClick={markAllAction.onClick}
              className="h-8 gap-1 px-2 text-caption font-semibold text-on-primary hover:bg-white/15 active:bg-white/25 hover:text-on-primary"
              leftIcon={<CheckCheck className="size-4 text-on-primary" />}
            >
              {markAllAction.label}
            </Button>
          ) : (
            <FieldNotifyBellButton to="/exe/notifications" from={path} />
          )
        }
      />
      <FieldPageActionsBar />
      {errorBanner ? (
        <div className="border-b border-border/60 bg-surface px-3 py-2">
          <Banner tone="warning" title={errorBanner} />
        </div>
      ) : null}
      <BottomNavContext.Provider value={showBottomNav}>
        <main
          className={cn(
            "mx-auto w-full max-w-3xl min-h-0 flex-1 overflow-y-auto px-3.5 pt-3.5",
            showBottomNav
              ? "pb-[calc(var(--layout-bottom-nav-height)+env(safe-area-inset-bottom)+1.25rem)]"
              : "pb-[calc(env(safe-area-inset-bottom)+1.25rem)]",
          )}
        >
          <Outlet />
        </main>
        {showBottomNav ? (
          <BottomNavigation items={items} activeId={activeId} />
        ) : null}
      </BottomNavContext.Provider>
    </div>
  );
}

export function PrintShell() {
  useEffect(() => {
    setDocumentTitle("In ấn");
  }, []);

  return (
    <div className="min-h-screen bg-white p-6 text-black print:p-0">
      <Outlet />
    </div>
  );
}

export function AuthShell() {
  useEffect(() => {
    setDocumentTitle("Đăng nhập");
  }, []);

  return (
    <div className="app-frame flex min-h-svh items-center justify-center overflow-y-auto bg-bg px-4">
      <Outlet />
    </div>
  );
}
